import React from "react";
import { ThemeProvider } from "./ThemeContext";
import { AuthProvider } from "./AuthContext";
import { SelectedModelProvider } from "./SelectedModelContext";
import { NotificationProvider } from "./NotificationContext";
import { StylesProvider } from "./StylesContext";
import { RecentGenerationsProvider } from "./RecentGenerationsContext";
import { GlobalGenerationProvider } from "./GlobalGenerationContext";
import { SunoCoverIndexProvider } from "./SunoCoverIndexContext";
import { SunoWavJobsProvider } from "./SunoWavJobsContext";
import { SunoCoverJobsProvider } from "./SunoCoverJobsContext";
import SunoCoverPickerSheet from "../pages/generate/components/SunoCoverPickerSheet";
import { SunoMidiJobsProvider } from "./SunoMidiJobsContext";
import SunoMidiViewerSheet from "../pages/generate/components/SunoMidiViewerSheet";
import { SunoVideoJobsProvider } from "./SunoVideoJobsContext";
import SunoVideoViewerSheet from "../pages/generate/components/SunoVideoViewerSheet";
import { SunoTimestampedJobsProvider } from "./SunoTimestampedJobsContext";
import SunoTimestampedViewerSheet from "../pages/generate/components/SunoTimestampedViewerSheet";
import { SunoStemJobsProvider } from "./SunoStemJobsContext";
import SunoStemsViewerSheet from "../pages/generate/components/SunoStemsViewerSheet";
import { SunoPersonasProvider } from "./SunoPersonasContext";
import { SunoReplaceJobsProvider } from "./SunoReplaceJobsContext";
import NotificationBridge from "../components/NotificationBridge";
import HistoryPersistenceBridge from "./HistoryPersistenceBridge";

export function AppProvider({ children }: { children: React.ReactNode }) {
  return (
    <ThemeProvider>
      <NotificationProvider>
        <AuthProvider>
          <NotificationBridge />
          <GlobalGenerationProvider>
            <SelectedModelProvider>
              <StylesProvider>
                <RecentGenerationsProvider>
                  {/*
                    Network-level save в историю генераций. Подписывается на
                    globalGen.status и сохраняет через snapshot из localStorage,
                    когда генерация завершилась в фоне (reload страницы или
                    пользователь на /files/..., где GenerateProvider не
                    смонтирован). Fire-and-forget — компонент без UI.
                  */}
                  <HistoryPersistenceBridge />
                  {/*
                    Lookup-индекс обложек Suno. Раздаёт getCover(audioUrl) для
                    старых GenerationCard без previewUrl (сохранённых до того
                    как бэк стал отдавать image_urls в TaskStatusResponse).
                  */}
                  <SunoCoverIndexProvider>
                    {/*
                      Фоновые WAV-конвертации. Переживают закрытие SunoActionsSheet,
                      хранят localStorage-кэш wav_url до того как backend деплой
                      начнёт его возвращать в API.
                    */}
                    <SunoWavJobsProvider>
                      {/*
                        Cover-job'ы: polling Suno-обложек + pending-choice state.
                        Picker-шторка всегда смонтирована на уровне App — при появлении
                        pendingChoice она открывается поверх всего остального UI.
                      */}
                      <SunoCoverJobsProvider>
                        <SunoCoverPickerSheet />
                        {/*
                          MIDI-job'ы: persistent polling + localStorage кэш
                          midi_data. Viewer открывается автоматически при
                          pendingViewer в SunoMidiJobsContext.
                        */}
                        <SunoMidiJobsProvider>
                          <SunoMidiViewerSheet />
                          {/*
                            Video-job'ы: persistent polling music-video.
                            Генерация идёт 3-5 мин, юзер не должен сидеть
                            в шторке всё это время. Viewer открывается сам
                            при pendingViewer.
                          */}
                          <SunoVideoJobsProvider>
                            <SunoVideoViewerSheet />
                            {/*
                              Timestamped lyrics (Lyrics+) — персистентный
                              polling + кэш строк. Karaoke-viewer открывается
                              автоматически по pendingViewer.
                            */}
                            <SunoTimestampedJobsProvider>
                              <SunoTimestampedViewerSheet />
                              {/*
                                Stem-job'ы: разделение на дорожки. Mureka синхронна,
                                Suno async — оба поллятся через /stems. Viewer с
                                плеерами дорожек открывается сам по pendingViewer.
                              */}
                              <SunoStemJobsProvider>
                                <SunoStemsViewerSheet />
                                {/*
                                  Replace-section job'ы: persistent polling замены
                                  фрагмента. Шит закрывается сразу после запуска,
                                  статус живёт пилюлей на строке исходного трека.
                                */}
                                <SunoReplaceJobsProvider>
                                  {/*
                                    Personas — глобальный список голосовых профилей.
                                    Нужен и в виджете (picker), и в Library (таб),
                                    поэтому живёт на верхнем уровне.
                                  */}
                                  <SunoPersonasProvider>
                                    {children}
                                  </SunoPersonasProvider>
                                </SunoReplaceJobsProvider>
                              </SunoStemJobsProvider>
                            </SunoTimestampedJobsProvider>
                          </SunoVideoJobsProvider>
                        </SunoMidiJobsProvider>
                      </SunoCoverJobsProvider>
                    </SunoWavJobsProvider>
                  </SunoCoverIndexProvider>
                </RecentGenerationsProvider>
              </StylesProvider>
            </SelectedModelProvider>
          </GlobalGenerationProvider>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}